import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { pushDefaultConsent, bootstrapGoogleTracking } from "@/lib/gtmBootstrap";
import { installTrackingEventLogger } from "@/lib/trackingEventLogger";
import { installGtmTroubleshooter } from "@/lib/gtmTroubleshooter";
import { installAnalyticsScriptPolicy } from "@/lib/analyticsScriptPolicy";
import { installInputModalityTracker } from "@/lib/inputModality";
import { runLocalStorageMigration } from "@/lib/localStorageMigration";

// Defensive stub for TikTok pixel: GTM-injected TikTok Events v3 tag occasionally
// calls Object._ttq_create before its own loader has defined ttq. Pre-stub so
// the call doesn't throw "Object._ttq_create is not a function" and break the
// tracking stack (Meta/Google/Bing pixels share the same JS task queue).
if (typeof window !== "undefined") {
  const O = Object as unknown as Record<string, unknown>;
  if (typeof O._ttq_create !== "function") {
    O._ttq_create = function () { /* no-op stub until real ttq loads */ };
  }
  const w = window as unknown as Record<string, unknown>;
  if (!w._ttq_create) w._ttq_create = O._ttq_create;
}

// One-time cleanup of legacy localStorage keys after any rename / namespace migration.
// Must run before any component reads localStorage.
runLocalStorageMigration();

// Hard guard: blockera okända gtag/gtm/Zaraz/GA-legacy-script innan exekvering.
// Måste köras FÖRE bootstrapGoogleTracking så vår egen GTM-container hinner
// markeras som tillåten via dataset.gtmContainer. GTM-bootstrap är redan
// uppskjuten till idle/interaction, så policy-installen behöver bara köra
// före den – men måste fortfarande vara klar innan ev. 3p-script försöker
// injiceras (de väntar på consent → minst en interaction). Säkert att defera.
installAnalyticsScriptPolicy();

const rootEl = document.getElementById("root")!;
const pathname = window.location.pathname;
const isHomeRoute = pathname === "/";
const isAuthSensitiveRoute = [
  "/logga-in",
  "/registrera",
  "/medlem",
  "/mitt-konto",
  "/reset-password",
].includes(pathname) || pathname.startsWith("/admin");

const shell = document.getElementById("shell");
if (shell) {
  document.body.appendChild(shell);
  if (isAuthSensitiveRoute) {
    shell.classList.add("shell-hidden");
  }
}

// On home route we INTENTIONALLY keep the shell visible until the React hero
// commits its own onLoad (in HeroCampaign). Hiding it on first RAF used to
// race ahead of React's paint and made the new React <img> a fresh LCP
// candidate — pushing lab LCP from ~2s to >10s on slow 4G Moto G in PSI.
// The shell hero is the LCP. React renders only the interactive overlay on top.

// Consent must fire before any analytics — keep synchronous
pushDefaultConsent();

// Defer GTM container load until idle / first user interaction. Consent Mode
// defaults were set synchronously in index.html so we never miss a consent
// signal, but loading gtm.js + 3rd-party pixels (Meta/Bing/TikTok/Pinterest)
// during initial render blocks the main thread and inflates TBT/LCP. We boot
// GTM as soon as the browser is idle OR the user interacts (whichever first).
//
// TBT-sensitive routes (heavy reptile category grids with filters) skip the
// idle boot entirely and wait either for first user interaction or an extended
// 8s timeout. Pixels still fire correctly for conversions because a visitor
// must interact before any add_to_cart / purchase event matters.
const __tbtSensitiveRoute =
  pathname === "/reptil/terrarier-teknik" ||
  pathname.startsWith("/reptil/terrarier-teknik/") ||
  pathname === "/reptil/belysning" ||
  pathname.startsWith("/reptil/belysning/");
let __gtmBooted = false;
const __bootGtmOnce = () => {
  if (__gtmBooted) return;
  __gtmBooted = true;
  bootstrapGoogleTracking();
};
// Only count *intentional* input as "interaction" — scroll/touchstart fire
// spuriously under Lighthouse/PSI simulation and used to boot GTM before LCP,
// which inflated lab-LCP on home from ~2s to 5.4s in PSI reports.
const __interactionEvents = ["pointerdown", "keydown"] as const;
const __onInteraction = () => {
  __bootGtmOnce();
  __interactionEvents.forEach((ev) =>
    window.removeEventListener(ev, __onInteraction, { capture: true } as EventListenerOptions),
  );
};
__interactionEvents.forEach((ev) =>
  window.addEventListener(ev, __onInteraction, { capture: true, passive: true, once: true }),
);
if (__tbtSensitiveRoute) {
  // Hard fallback only after 8s so initial render & hydration finish in peace.
  setTimeout(__bootGtmOnce, 8000);
} else if (isHomeRoute) {
  // Home is LCP-critical. GTM and child pixels (Meta/Bing/TikTok/etc.) can add
  // long tasks before the hero settles, so let the page become interactive first.
  // 8000ms keeps us comfortably past PSI's mobile LCP window (~5.4s on slow 4G).
  // Any real click/keypress still boots GTM immediately via the listeners above.
  setTimeout(__bootGtmOnce, 8000);
} else if ("requestIdleCallback" in window) {
  (window as unknown as { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
    .requestIdleCallback(__bootGtmOnce, { timeout: 5000 });
} else {
  setTimeout(__bootGtmOnce, 2500);
}


// Render React first, then init non-critical scripts in idle time.
// Deferring trackingEventLogger / gtmTroubleshooter / inputModality past first
// paint reduces TBT on the LCP-critical main thread; they only need to be
// installed before user interaction triggers GTM/consent flow (which itself
// is gated on idle + interaction).
createRoot(rootEl).render(<App />);

if (isHomeRoute && shell) {
  requestAnimationFrame(() => {
    shell.classList.add("shell-chrome-hidden");
  });
}

const deferInit = () => {
  installInputModalityTracker();
  installTrackingEventLogger();
  installGtmTroubleshooter();
  // Real-user Web Vitals (LCP/CLS/INP/FCP/TTFB) → dataLayer → GA4 via GTM.
  // Lets us watch real LCP per route/session and detect regressions outside
  // lab-only PSI runs.
  import("@/lib/webVitals")
    .then(({ installWebVitalsReporter }) => installWebVitalsReporter())
    .catch(() => { /* never break the app */ });
  import("@/lib/referrerRestore").then(({ initReferrerRestore }) => initReferrerRestore());
  import("@/lib/gclidCapture").then(({ initAdClickCapture }) => initAdClickCapture());
  // GTM/gtag rogue-injection health check — runs once a few seconds after load
  // so Cloudflare Zaraz / Shopify channels / extensions have had time to inject.
  setTimeout(() => {
    import("@/lib/gtmHealthCheck")
      .then(({ runAndReportGtmHealth }) => runAndReportGtmHealth())
      .catch(() => { /* never break the app */ });
  }, 5000);
};

if ("requestIdleCallback" in window) {
  (window as unknown as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(deferInit);
} else {
  setTimeout(deferInit, 50);
}

if (!isHomeRoute && !isAuthSensitiveRoute) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const s = document.getElementById("shell");
      if (s) s.classList.add("shell-hidden");
    });
  });
}
